import { StateMachine } from "@/core/arch/state_machine";
import { normalizeForSearch } from "@cpos/shared/search";
import { searchDocs } from "@/application/search/matcher";
import { isMissingSearchIndex } from "@/application/search/search_index_repository";
import type {
  SearchDoc,
  SearchIndexArtifact,
} from "@/application/search/types";
import type { BrowseResult } from "./merchants_repository";
import type {
  MerchantRow,
  MerchantFilters,
  OverallStage,
  OnboardingStage,
  IdentityStage,
} from "./types";
import { defaultFilters } from "./types";
import { inWindow, parseListParams } from "./merchant_list_params";

/** Merchants shown per page. Paging is over matches, so pages are never short. */
export const PAGE_SIZE = 50;

// ── Collaborators ────────────────────────────────────────────────────────────

/** The prebuilt index this list reads from. */
export interface MerchantIndexSource {
  load: () => Promise<SearchIndexArtifact>;
  refresh: () => Promise<SearchIndexArtifact>;
}

/** Live read used only when no index has been published yet. */
export interface MerchantBrowseFallback {
  browse: (nextToken?: string) => Promise<BrowseResult>;
}

// ── State ─────────────────────────────────────────────────────────────────────

export interface MerchantsListState {
  /** The current page of the filtered, ranked result set. */
  items: MerchantRow[];
  query: string;
  filters: MerchantFilters;
  status: "idle" | "loading" | "loaded" | "error";
  error?: string;
  /** Total matches across the whole base, not just this page. */
  totalMatches: number;
  hasNext: boolean;
  hasPrev: boolean;
  /** When the index was built, for the staleness label. Null in fallback mode. */
  generatedAt: string | null;
  /**
   * Why signup and Pro window filters cannot match, or null when they can.
   * `index-predates-times`: an index loaded but no document carries the
   * signup/Pro fields yet (it was built before they existed). `no-index`:
   * serving the degraded live page, which carries no Pro times or
   * billing-resolved plan, so those filters are not applied.
   */
  windowFilterNotice: WindowFilterNotice | null;
  /** False when serving a degraded live page because no index exists yet. */
  indexAvailable: boolean;
  /**
   * Option counts for each filterable column, over everything the query
   * matched before filters were applied — so the counts describe the whole
   * base rather than the visible page.
   */
  facets: MerchantFacets;
}

export type WindowFilterNotice = "index-predates-times" | "no-index";

export interface MerchantFacets {
  overallStage: Map<string, number>;
  onboardingStage: Map<string, number>;
  identityStage: Map<string, number>;
  subscriptionStatus: Map<string, number>;
}

function emptyFacets(): MerchantFacets {
  return {
    overallStage: new Map(),
    onboardingStage: new Map(),
    identityStage: new Map(),
    subscriptionStatus: new Map(),
  };
}

function countFacets(docs: SearchDoc[]): MerchantFacets {
  const facets = emptyFacets();
  const bump = (map: Map<string, number>, value: string | null) => {
    if (value === null) return;
    map.set(value, (map.get(value) ?? 0) + 1);
  };
  for (const doc of docs) {
    bump(facets.overallStage, doc.overallStage);
    bump(facets.onboardingStage, doc.onboardingStage);
    bump(facets.identityStage, doc.identityStage);
    bump(facets.subscriptionStatus, doc.subscriptionStatus);
  }
  return facets;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Projects a live-browsed row into a document so degraded mode can search and
 * filter with the same code as the index. Only the fields the row carries are
 * searchable — there are no owner names or emails on a browse result.
 *
 * The id stays out of the haystack for the same reason it does in the indexed
 * documents: it would let a partial fragment prefix-match and surface an
 * unrelated merchant. Exact-id lookup reads `doc.id` directly, so it still
 * works here.
 */
function toDoc(row: MerchantRow): SearchDoc {
  return {
    id: row.id,
    businessName: row.businessName,
    ownerNames: [],
    emails: [],
    stripeAcct: null,
    plan: row.plan,
    overallStage: row.overallStage,
    onboardingStage: row.onboardingStage,
    identityStage: row.identityStage,
    subscriptionStatus: row.subscriptionStatus,
    createdAt: row.createdAt,
    proSince: row.proSince,
    updatedAt: row.updatedAt,
    haystack: normalizeForSearch(row.businessName),
  };
}

function toRow(doc: SearchDoc): MerchantRow {
  return {
    id: doc.id,
    businessName: doc.businessName,
    plan: doc.plan,
    overallStage: doc.overallStage,
    onboardingStage: doc.onboardingStage,
    identityStage: doc.identityStage,
    subscriptionStatus: doc.subscriptionStatus,
    createdAt: doc.createdAt ?? null,
    proSince: doc.proSince ?? null,
    updatedAt: doc.updatedAt,
  };
}

/**
 * True when documents exist but none carries the `createdAt` key, which means
 * the index was built before signup and Pro times were added. Presence of the
 * key, not a value: a merchant with no Pro conversion has `proSince: null`, and
 * an index where no merchant has a value is still a current one.
 */
function indexPredatesTimes(docs: SearchDoc[]): boolean {
  return docs.length > 0 && !docs.some((d) => "createdAt" in d);
}

// ── BLoC ──────────────────────────────────────────────────────────────────────

/**
 * The merchants list, served from the prebuilt search index.
 *
 * Searching, filtering, and paging are all local operations over one array.
 * That is what makes paging land on *matches* rather than on scanned rows: the
 * previous implementation filtered server-side after each 500-row page was
 * read, so a page matching three merchants returned three rows and a cursor,
 * and lifecycle filters only ever saw whichever rows that page happened to
 * contain.
 */
export class MerchantsListBloc extends StateMachine<MerchantsListState> {
  private _docs: SearchDoc[] = [];
  private _matches: MerchantRow[] = [];
  private _page = 0;
  private _loadToken = 0;

  constructor(
    private readonly index: MerchantIndexSource,
    private readonly fallback: MerchantBrowseFallback,
  ) {
    super({
      items: [],
      query: "",
      filters: defaultFilters(),
      status: "idle",
      totalMatches: 0,
      hasNext: false,
      hasPrev: false,
      generatedAt: null,
      indexAvailable: true,
      windowFilterNotice: null,
      facets: emptyFacets(),
    });
  }

  // ── Public actions ──────────────────────────────────────────────────────────

  /** Load the index (or the live fallback) and show the first page. */
  async load(): Promise<void> {
    await this._loadIndex(() => this.index.load());
  }

  /** Re-read the index, bypassing the cache, keeping the query and filters. */
  async refresh(): Promise<void> {
    await this._loadIndex(() => this.index.refresh());
  }

  /** Set the search query and return to the first page. */
  async setQuery(query: string): Promise<void> {
    this.setState({ ...this.state, query });
    this._recompute();
  }

  /** Apply a filter patch and return to the first page. */
  async setFilter(patch: Partial<MerchantFilters>): Promise<void> {
    this.setState({
      ...this.state,
      filters: { ...this.state.filters, ...patch },
    });
    this._recompute();
  }

  /**
   * Sets the URL-driven filters (signup and Pro windows, plan) from a link's
   * query string, leaving the stage and subscription filters alone. Malformed
   * parameters are ignored, so each is reset to unset rather than rejected.
   */
  async applyUrlParams(params: URLSearchParams): Promise<void> {
    const patch = parseListParams(params);
    const f = this.state.filters;
    if (
      f.signedUpFrom === patch.signedUpFrom &&
      f.signedUpTo === patch.signedUpTo &&
      f.proSinceFrom === patch.proSinceFrom &&
      f.proSinceTo === patch.proSinceTo &&
      f.plan === patch.plan
    ) {
      return;
    }
    if (this.state.status === "idle" || this.state.status === "loading") {
      // Paging an empty match set now would flash an empty table; the load
      // that is in flight recomputes with these filters once it lands.
      this.setState({
        ...this.state,
        filters: { ...this.state.filters, ...patch },
      });
      return;
    }
    await this.setFilter(patch);
  }

  /**
   * Clears the stage, subscription and URL-driven filters in one recompute,
   * keeping the search query. The URL's own parameters are cleared by the
   * caller; the follow-up `applyUrlParams` then finds nothing left to change.
   */
  async resetFilters(): Promise<void> {
    const { searchQuery: _kept, ...cleared } = defaultFilters();
    void _kept;
    await this.setFilter(cleared);
  }

  /** Advance one page. */
  async nextPage(): Promise<void> {
    if (!this.state.hasNext) return;
    this._page += 1;
    this._emitPage();
  }

  /** Return to the previous page. */
  async prevPage(): Promise<void> {
    if (!this.state.hasPrev) return;
    this._page -= 1;
    this._emitPage();
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  private async _loadIndex(
    read: () => Promise<SearchIndexArtifact>,
  ): Promise<void> {
    const token = ++this._loadToken;
    this.setState({ ...this.state, status: "loading", error: undefined });

    try {
      const artifact = await read();
      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter
      if (token !== this._loadToken) return;
      this._docs = artifact.docs;
      this.setState({
        ...this.state,
        generatedAt: artifact.generatedAt,
        indexAvailable: true,
        windowFilterNotice: indexPredatesTimes(artifact.docs)
          ? "index-predates-times"
          : null,
      });
      this._recompute();
    } catch (err) {
      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter
      if (token !== this._loadToken) return;
      if (isMissingSearchIndex(err)) {
        await this._loadFallback(token);
        return;
      }
      this.setState({
        ...this.state,
        status: "error",
        error: err instanceof Error ? err.message : "Failed to load merchants",
        hasNext: false,
        hasPrev: false,
      });
    }
  }

  /**
   * Serves one live page when no index has been published yet, so a fresh
   * environment shows merchants rather than an empty table. Search and filters
   * still work, but only across this page — the state carries
   * `indexAvailable: false` so the view can say so.
   */
  private async _loadFallback(token: number): Promise<void> {
    try {
      const page = await this.fallback.browse();
      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter
      if (token !== this._loadToken) return;
      // Projected into documents so searching and filtering run through the
      // same path as the index, just over a single page of rows.
      this._docs = page.items.map(toDoc);
      this.setState({
        ...this.state,
        generatedAt: null,
        indexAvailable: false,
        windowFilterNotice: "no-index",
      });
      this._recompute();
    } catch (err) {
      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter
      if (token !== this._loadToken) return;
      this.setState({
        ...this.state,
        status: "error",
        error: err instanceof Error ? err.message : "Failed to load merchants",
        hasNext: false,
        hasPrev: false,
      });
    }
  }

  /** Re-derive the full match set from the query and filters, then page it. */
  private _recompute(): void {
    const query = this.state.query.trim();
    const ranked = searchDocs(this._docs, query).map((hit) => hit.doc);

    // A blank query has no ranking signal, so show the most recently active
    // merchants rather than an alphabetical wall.
    const ordered =
      query === ""
        ? [...ranked].sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0))
        : ranked;

    this.setState({ ...this.state, facets: countFacets(ordered) });
    this._matches = this._applyFilters(ordered).map(toRow);
    this._page = 0;
    this._emitPage();
  }

  private _emitPage(): void {
    const start = this._page * PAGE_SIZE;
    this.setState({
      ...this.state,
      items: this._matches.slice(start, start + PAGE_SIZE),
      // A failed load stays failed: re-paging an empty or stale match set must
      // not turn the error into a loaded, empty-looking table.
      ...(this.state.status === "error"
        ? {}
        : { status: "loaded" as const, error: undefined }),
      totalMatches: this._matches.length,
      hasNext: start + PAGE_SIZE < this._matches.length,
      hasPrev: this._page > 0,
    });
  }

  private _applyFilters(docs: SearchDoc[]): SearchDoc[] {
    const {
      overallStages,
      onboardingStages,
      identityStages,
      subscriptionStatuses,
      plan,
      signedUpFrom,
      signedUpTo,
      proSinceFrom,
      proSinceTo,
    } = this.state.filters;
    let result = docs;

    // A live page carries the profile's raw plan, not the billing-resolved
    // one the index and the wrap-up use, so the plan filter waits for an index.
    if (plan !== null && this.state.indexAvailable) {
      result = result.filter((m) => m.plan === plan);
    }

    if (signedUpFrom !== null || signedUpTo !== null) {
      result = result.filter((m) =>
        inWindow(m.createdAt, signedUpFrom, signedUpTo),
      );
    }

    if (proSinceFrom !== null || proSinceTo !== null) {
      result = result.filter((m) =>
        inWindow(m.proSince, proSinceFrom, proSinceTo),
      );
    }

    if (overallStages.length > 0) {
      const set = new Set<OverallStage>(overallStages);
      result = result.filter(
        (m) => m.overallStage !== null && set.has(m.overallStage),
      );
    }

    if (onboardingStages.length > 0) {
      const set = new Set<OnboardingStage>(onboardingStages);
      result = result.filter(
        (m) => m.onboardingStage !== null && set.has(m.onboardingStage),
      );
    }

    if (identityStages.length > 0) {
      const set = new Set<IdentityStage>(identityStages);
      result = result.filter(
        (m) => m.identityStage !== null && set.has(m.identityStage),
      );
    }

    if (subscriptionStatuses.length > 0) {
      const set = new Set(subscriptionStatuses);
      result = result.filter(
        (m) => m.subscriptionStatus !== null && set.has(m.subscriptionStatus),
      );
    }

    return result;
  }
}
